import { t } from "@lingui/core/macro"
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query"
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router"
import {
  ArrowUpDown,
  Download,
  GitCompare,
  History,
  Languages,
  Layers,
  Globe,
  List,
  Package,
  Ruler,
  Settings2,
  TestTube2,
  X,
  XCircle,
  Grid3X3,
} from "lucide-react"
import { useCallback, useState } from "react"
import { toast } from "sonner"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { ProjectPickerDialog } from "@/features/config-preview/components/ProjectPickerDialog"
import { useTypedFlag } from "@/features/feature-flags/registry"
import { LIST_OF_MATERIALS_IN_NAVIGATION } from "@/features/list-of-materials/navigation"
import { SectionCard } from "@/features/store-configuration/components/SectionCard"
import { useComparisonMode } from "@/features/store-configuration/hooks/useComparisonMode"
import {
  getConfigurationVersionFn,
  listConfigurationVersionsFn,
  countProjectsUsingConfigVersion,
} from "@/features/store-configuration/server/storeConfiguration"
import { CURRENT_SCHEMA_VERSION } from "@/features/store-configuration/types/importSchema"
import {
  getCategoryColorForGroup,
  getContrastColor,
} from "@/features/store-configuration/utils/categoryColors"
import { usePermissions } from "@/hooks/usePermissions"
import { storeConfigurationQueryKeys } from "@/lib/queryKeys"

const configurationVersionQuery = (versionId: string) =>
  queryOptions({
    queryKey: storeConfigurationQueryKeys.json(versionId),
    queryFn: () => getConfigurationVersionFn({ data: { id: versionId } }),
  })

const configurationVersionsListQuery = () =>
  queryOptions({
    queryKey: storeConfigurationQueryKeys.list({ includeArchived: false }),
    queryFn: () => listConfigurationVersionsFn({ data: { includeArchived: false } }),
  })

const projectsUsingVersionQuery = (configVersionId: string) =>
  queryOptions({
    queryKey: ["projects", "count", "byConfigVersion", configVersionId],
    queryFn: () => countProjectsUsingConfigVersion({ data: { configVersionId } }),
  })

export const Route = createFileRoute(
  "/(authenticated)/settings/configuration-versions/$versionId/",
)({
  loader: ({ context, params }) => {
    void context.queryClient.ensureQueryData(configurationVersionQuery(params.versionId))
    void context.queryClient.ensureQueryData(configurationVersionsListQuery())
    void context.queryClient.ensureQueryData(projectsUsingVersionQuery(params.versionId))
  },
  component: StoreConfigurationDetailPage,
})

function StoreConfigurationDetailPage() {
  const { versionId } = Route.useParams()
  const navigate = useNavigate()
  const { data: config } = useSuspenseQuery(configurationVersionQuery(versionId))
  const { data: versions } = useSuspenseQuery(configurationVersionsListQuery())
  const { data: projectCount } = useSuspenseQuery(projectsUsingVersionQuery(versionId))
  const { hasPermission } = usePermissions()
  const canViewAuditLog = hasPermission("auditLog", "read").allowed
  const { value: configPreview } = useTypedFlag("config-preview")

  // Comparison mode state with diff computation
  const {
    comparisonVersionId,
    diff,
    isComparing,
    isLoadingComparison,
    setComparisonVersion,
    clearComparison,
  } = useComparisonMode({ currentData: config })

  // Filter out current version from comparison dropdown
  const otherVersions = versions.filter((v) => v.id !== versionId)

  // Preview mode state
  const [showProjectPicker, setShowProjectPicker] = useState(false)

  // Handler for testing on project
  const handleTestOnProject = useCallback(
    (projectId: string) => {
      // Navigate to the project optimize page with preview mode enabled
      void navigate({
        to: "/projects/edit/$projectId/optimize",
        params: { projectId },
        search: { previewConfigVersion: versionId },
      })
    },
    [versionId, navigate],
  )

  const handleExportJson = useCallback(() => {
    if (!config) return

    try {
      const exportData = { ...config, schemaVersion: CURRENT_SCHEMA_VERSION }
      const jsonStr = JSON.stringify(exportData, null, 2)
      const blob = new Blob([jsonStr], { type: "application/json" })
      const url = URL.createObjectURL(blob)

      // Create download link
      const a = document.createElement("a")
      a.href = url
      // Use version label for filename, sanitized
      const filename = `store-config-${config.storeConfiguration.version.replace(/[^a-zA-Z0-9.-]/g, "-")}.json`
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)

      toast.success(t`Configuration exported successfully`)
    } catch (error) {
      console.error("Export failed:", error)
      toast.error(t`Failed to export configuration`)
    }
  }, [config])

  if (!config) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <XCircle className="mb-4 size-12 text-red-500" />
        <h2 className="mb-2 text-xl font-semibold">{t`Configuration not found`}</h2>
        <p className="mb-4 text-neutral-500">
          {t`The requested configuration version does not exist.`}
        </p>
        <Button onClick={() => void navigate({ to: "/settings/configuration-versions" })}>
          {t`Back to versions`}
        </Button>
      </div>
    )
  }

  const storeConfig = config.storeConfiguration
  const globalRules = storeConfig.globalRules

  const categoriesCount = Object.keys(globalRules.categories).length
  const bandwidthsCount = Object.keys(globalRules.bandwidths).length
  const prohibitedRulesCount = globalRules.prohibitedRules?.length ?? 0
  const wallOrderPriorityCount = globalRules.wallOrderPriority.length

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  return (
    <div className="space-y-6">
      {/* Actions bar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-sm text-neutral-500 dark:text-neutral-300">
            {t`Last updated: ${formatDate(storeConfig.lastUpdated)}`}
          </p>
          <div className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 dark:border-blue-800 dark:bg-blue-900/20">
            <Package className="size-4 text-blue-600 dark:text-blue-400" />
            <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
              {projectCount === 1
                ? t`${projectCount} project using this version`
                : t`${projectCount} projects using this version`}
            </span>
            {projectCount > 0 && (
              <Link
                to="/projects"
                search={{ configVersionId: versionId }}
                className="ml-1 text-xs font-semibold text-blue-600 hover:underline dark:text-blue-400"
              >
                {t`View →`}
              </Link>
            )}
          </div>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {/* Compare to dropdown */}
          <div className="flex items-center gap-2">
            <Select value={comparisonVersionId ?? ""} onValueChange={setComparisonVersion}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <GitCompare className="mr-2 size-4 shrink-0" />
                <SelectValue placeholder={t`Compare to...`} />
              </SelectTrigger>
              <SelectContent>
                {otherVersions.map((version) => (
                  <SelectItem key={version.id} value={version.id}>
                    {version.versionLabel}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isComparing && (
              <Button
                variant="outline"
                size="icon"
                onClick={clearComparison}
                title={t`Clear comparison`}
                className="shrink-0"
              >
                <X className="size-4" />
              </Button>
            )}
            {isLoadingComparison && <span className="text-sm text-neutral-500">Loading...</span>}
          </div>
          {configPreview && (
            <Button variant="outline" onClick={() => setShowProjectPicker(true)}>
              <TestTube2 className="mr-2 size-4 shrink-0" />
              <span className="truncate">{t`Test on project...`}</span>
            </Button>
          )}
          {canViewAuditLog && (
            <Link
              to="/settings/audit"
              search={{
                entityType: "store-configuration",
                entityId: versionId,
              }}
            >
              <Button variant="outline">
                <History className="mr-2 size-4 shrink-0" />
                <span className="truncate">{t`Audit Log`}</span>
              </Button>
            </Link>
          )}
          <Button variant="outline" onClick={handleExportJson}>
            <Download className="mr-2 size-4 shrink-0" />
            <span className="truncate">{t`Export JSON`}</span>
          </Button>
        </div>
      </div>

      {/* Global Constraints Banner */}
      {globalRules.globalConstraints && (
        <Card className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20">
          <CardContent className="py-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
              <Badge variant="info" className="w-fit">{t`Global Constraints`}</Badge>
              {globalRules.globalConstraints.maxRowSpread && (
                <span className="text-sm">
                  {t`Max Row Spread`}: <strong>{globalRules.globalConstraints.maxRowSpread}</strong>
                </span>
              )}
              {globalRules.globalConstraints.notes && (
                <span className="text-sm text-neutral-600 dark:text-neutral-400">
                  {globalRules.globalConstraints.notes}
                </span>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Configuration sections, grouped like the side navigation in route.tsx */}
      <section className="space-y-3">
        <h2 className="text-lg font-semibold">{t`General`}</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-2">
          <SectionCard
            title={t`Global`}
            description={t`Supported locales and constraints that apply to the whole configuration`}
            icon={Settings2}
            onClick={() =>
              void navigate({
                to: "/settings/configuration-versions/$versionId/edit",
                params: { versionId },
                search: { section: "global-constraints" },
              })
            }
          />
          <SectionCard
            title={t`Translations`}
            description={t`Localized labels for merchandise groups per locale`}
            icon={Languages}
            onClick={() =>
              void navigate({
                to: "/settings/configuration-versions/$versionId/translations",
                params: { versionId },
              })
            }
          />
        </div>
      </section>

      <section className="space-y-3">
        <h2 className="text-lg font-semibold">{t`Categories & Bandwidths`}</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-2">
          <SectionCard
            title={t`Categories`}
            description={t`Product categories with display names, colors, and merchandise group assignments`}
            icon={Layers}
            count={categoriesCount}
            diffSummary={isComparing ? diff?.categories.summary : null}
            onClick={() =>
              void navigate({
                to: "/settings/configuration-versions/$versionId/edit",
                params: { versionId },
                search: { section: "categories" },
              })
            }
          />
          <SectionCard
            title={t`Bandwidths`}
            description={t`Wall and gondola space allocations per merchandise group with scaling rules`}
            icon={Ruler}
            count={bandwidthsCount}
            diffSummary={isComparing ? diff?.bandwidths.summary : null}
            onClick={() =>
              void navigate({
                to: "/settings/configuration-versions/$versionId/edit",
                params: { versionId },
                search: { section: "bandwidths" },
              })
            }
          />
          <SectionCard
            title={t`Category Order`}
            description={t`Order of merchandise groups in the Design Blueprint sidebar and Excel exports`}
            icon={ArrowUpDown}
            onClick={() =>
              void navigate({
                to: "/settings/configuration-versions/$versionId/edit",
                params: { versionId },
                search: { section: "category-sort-order" },
              })
            }
          />
          <SectionCard
            title={t`Country specific bandwidths`}
            description={t`Per-country instructions shown on the optimize screen`}
            icon={Globe}
            onClick={() =>
              void navigate({
                to: "/settings/configuration-versions/$versionId/edit",
                params: { versionId },
                search: { section: "country-hints" },
              })
            }
          />
        </div>
      </section>

      <section className="space-y-3">
        <h2 className="text-lg font-semibold">{t`Layout Rules`}</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-2">
          <SectionCard
            title={t`Wall Order Priority`}
            description={t`Priority sequence for placing merchandise groups on walls`}
            icon={List}
            count={wallOrderPriorityCount}
            diffSummary={isComparing ? diff?.wallOrderPriority.summary : null}
            onClick={() =>
              void navigate({
                to: "/settings/configuration-versions/$versionId/edit",
                params: { versionId },
                search: { section: "wall-order" },
              })
            }
          />
          <SectionCard
            title={t`Layout Rules`}
            description={t`Planner groups, special roles, row mixes, zone repairs (sent verbatim to the gondola planner). Falls back to server defaults when empty.`}
            icon={Settings2}
            onClick={() =>
              void navigate({
                to: "/settings/configuration-versions/$versionId/edit",
                params: { versionId },
                search: { section: "layout-rules" },
              })
            }
          />
          <SectionCard
            title={t`Prohibited Rules`}
            description={t`Hard constraints preventing certain merchandise group placements`}
            icon={XCircle}
            count={prohibitedRulesCount}
            diffSummary={isComparing ? diff?.prohibitedRules.summary : null}
            onClick={() =>
              void navigate({
                to: "/settings/configuration-versions/$versionId/edit",
                params: { versionId },
                search: { section: "prohibited-rules" },
              })
            }
          />
          <SectionCard
            title={t`Gondola Arrangements`}
            description={t`Placement patterns for each category on gondola shelving units`}
            icon={Package}
            diffSummary={isComparing ? diff?.gondolaArrangements.summary : null}
            onClick={() =>
              void navigate({
                to: "/settings/configuration-versions/$versionId/edit",
                params: { versionId },
                search: { section: "gondola-arrangements" },
              })
            }
          />
        </div>
      </section>

      {LIST_OF_MATERIALS_IN_NAVIGATION && (
        <section className="space-y-3">
          <h2 className="text-lg font-semibold">{t`List of Materials (LoM)`}</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-2">
            <SectionCard
              title={t`List of Materials`}
              description={t`Shelf codes, coding, rules, articles and extras of this version, and the list per store`}
              icon={Grid3X3}
              onClick={() =>
                void navigate({ to: "/list-of-materials", search: { version: versionId } })
              }
            />
          </div>
        </section>
      )}

      {/* Quick Data Preview */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Categories Preview */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t`Categories Preview`}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {Object.entries(globalRules.categories)
                .slice(0, 5)
                .map(([slug, cat]) => (
                  <div
                    key={slug}
                    className="flex items-center justify-between rounded-lg border p-2"
                  >
                    <div className="flex items-center gap-2">
                      <div className="size-4 rounded" style={{ backgroundColor: cat.color }} />
                      <span className="font-medium">{cat.displayName}</span>
                      <Badge variant="outline" className="ml-2">
                        {cat.id}
                      </Badge>
                    </div>
                    <span className="text-sm text-neutral-500">
                      {cat.merchandiseGroups.length === 1
                        ? t`1 group`
                        : t`${cat.merchandiseGroups.length} groups`}
                    </span>
                  </div>
                ))}
              {categoriesCount > 5 && (
                <Link
                  to="/settings/configuration-versions/$versionId/edit"
                  params={{ versionId }}
                  search={{ section: "categories" }}
                  className="block text-center text-sm text-blue-600 hover:underline dark:text-blue-400"
                >
                  {t`and ${categoriesCount - 5} more...`}
                </Link>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Wall Order Preview */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t`Wall Order Priority`}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {globalRules.wallOrderPriority.map((code, index) => {
                const color = getCategoryColorForGroup(code, globalRules.categories)
                return (
                  <Badge
                    key={code}
                    variant="secondary"
                    className="font-mono"
                    style={
                      color ? { backgroundColor: color, color: getContrastColor(color) } : undefined
                    }
                  >
                    {index + 1}. {code}
                  </Badge>
                )
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Project Picker Dialog for Testing */}
      {configPreview && (
        <ProjectPickerDialog
          open={showProjectPicker}
          onOpenChange={setShowProjectPicker}
          onSelectProject={handleTestOnProject}
        />
      )}
    </div>
  )
}
